/* eslint-disable max-lines */
import tokens from '@kluein/design-tokens';
import { Box, Tip } from '@kluein/klue-ui';
import { Paper } from '@mantine/core';
import { CalendarBlank, X } from '@phosphor-icons/react';
import throttle from 'lodash/throttle';
import {
  lazy,
  Suspense,
  createRef,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import CardScheduleModal from 'components/common/card-schedule-modal/CardScheduleModal';
import { useAuth } from 'contexts/auth';
import { useAppUIContext } from 'contexts/ui';
import { BulkEditOption } from 'contexts/ui/bulk-edit-context/bulkEditContext.types';
import { useEmbedded } from 'contexts/ui/embedded-context';
import { useCardViewer } from 'hooks/use-card-viewer';
import sanitize from 'klue-html/sanitizer';
import {
  AnalyticsAction,
  AnalyticsCategory,
  useAnalytics,
} from 'lib/analytics';
import TEST_IDS from 'test-ids';

import CardSelector from './cardSelector';
import { useCardInteractionLog } from './hooks';
import useUpdateVisibilityGroups from './hooks/use-update-visibility-groups/useUpdateVisibilityGroups';
import { getStickyStyles } from './KlueCard.styles';
import { handleTableClicks, initTable } from './KlueCard.utils';
import {
  KlueCardContent,
  KlueCardHeader,
  KlueCardToolbar,
  KlueCardSentiment,
  KlueCardFeedbackModal,
  KlueCardInfo,
  KlueCardTagEditor,
  KlueCardVisibilityGroups,
} from './partials';
import { getVisibilityGroupColors } from './partials/klue-card-visibility-groups/helpers';

import type { KlueCardProps } from './KlueCard.types';
import type { GetCardPermissionCardsDataResult } from 'pages/profile/partials/sideAction/bulkEdit/cardPermissions/CardPermissions.utils';

const KlueCardSources = lazy(
  () => import('./partials/klue-card-sources/KlueCardSources'),
);

const KLUE_CARD_SIZE = {
  normal: tokens.size.large,
  wide: tokens.size.xxlarge,
};
const KLUE_CARD_INTERNAL_GUTTER = parseInt(tokens.spacing.large);
const KLUE_CARD_CUSTOM_MARGIN_CONTENT = `${tokens.spacing.large} 0 ${tokens.spacing.xxlarge} 0`;
export const KLUE_CARD_INTERNAL_SIZE = {
  normal: `${parseInt(tokens.size.large) - KLUE_CARD_INTERNAL_GUTTER}px`,
  wide: `${parseInt(tokens.size.xlarge) - KLUE_CARD_INTERNAL_GUTTER}px`,
};

const StyledTableScrollWrap = styled.div`
  /* Bounded width + overflow-x lets wide tables scroll horizontally inside the
     zoom modal. Do NOT add 'min-width: fit-content' — it forces the container
     to the table's full intrinsic width (min-width beats max-width per spec),
     so the container never overflows-to-scroll and the modal clips the
     right-hand columns. 'overflow-x: auto' here mirrors the in-card
     .table-scrollwrap wrapper. */
  width: 100%;
  max-width: 100vw;
  overflow-x: auto;
  resize: horizontal;
  table {
    margin: 0 !important;
  }
`;

const VisibilityGroupBorderWidth = tokens.borderWidth.small;

const STICKY_TRANSITION = 'opacity 100ms ease-in-out';
const USER_INACTIVITY_MS = 2000;
const THROTTLE_MS = 250;

type ElevationKey = keyof typeof tokens.boxShadow;

const KlueCard = (props: KlueCardProps) => {
  const {
    cardData,
    rivalName = '',
    boardName = '',
    className = '',
    initialHeight = 'auto',
    isWide = false,
    overflow,
    isBattlecardView = false,
    isSearchView = false,
    isCardViewerContent = false,
    hideMenus = false,
    hideBattlecardIndicator = true,
    showCardSentiment = false,
    fluid = false,
    bulkEditData = {
      isSelectable: false,
      isSelected: false,
    },
    onTitleHover,
    variant = 'default',
    onTitleClick,
    profileId,
    rivalId,
    padStyles,
    rank,
  } = props;

  // Image zoom handler
  const cardContainer = createRef<HTMLDivElement>();
  const { modal } = useAppUIContext();
  const isEmbedded = useEmbedded();
  const {
    showCardInfo,
    isCardSentimentEnabled,
    isCurator,
    isNewCardEditorEnabled,
    isTagVisible,
  } = useAuth();
  const { open: openCardViewer } = useCardViewer();
  const navigate = useNavigate();
  const { t } = useTranslation(['Card']);
  const { id: cardId } = cardData ?? {};

  const hasSchedule = !!cardData?.cardSchedule;
  const hasActiveSchedule =
    !!cardData?.cardSchedule && cardData.cardSchedule.frequencyDays !== null;
  const isContentEmpty =
    (!Array.isArray(cardData?.data?.textJson) ||
      cardData.data.textJson.length === 0) &&
    (!cardData?.data?.textHtml || cardData.data.textHtml.trim() === '');
  // Show the "Klue AI is processing…" placeholder on any empty card that
  // has an active schedule — not just cards in the AI Battlecard lane.
  // CSV-imported scheduled cards land in their own topic lanes and are in
  // the same "waiting for Klue AI to fill it" state until the scheduler
  // populates them. Frequency is piped through so the copy names the
  // actual cadence rather than a hard-coded "monthly by default".
  const showAiProcessingPlaceholder = hasActiveSchedule && isContentEmpty;
  const aiProcessingFrequencyDays =
    cardData?.cardSchedule?.frequencyDays ?? null;
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const cardName = cardData?.data?.name;
  const hasLoaded = Boolean(cardId);
  const [feedbackModal, setFeedbackModal] = useState(false);
  const { onVisibilityGroupSelected } = useUpdateVisibilityGroups();

  const [showCardSources, setShowCardSources] = useState<boolean>(
    !!isCardViewerContent,
  );
  const [cardElevation, setCardElevation] = useState<ElevationKey>('xsmall');

  const CARD_WIDTH = isWide ? KLUE_CARD_SIZE.wide : KLUE_CARD_SIZE.normal;
  const { isSelectable, isSelected, bulkEditOption, onToggleSelected } =
    bulkEditData;

  const isEditingCardPermissions =
    bulkEditOption === BulkEditOption.CardPermissions;
  const showTags =
    isTagVisible && (!isSelectable || bulkEditOption === BulkEditOption.Tags);

  const { logEvent } = useAnalytics();
  const logCardInteraction = useCardInteractionLog();

  const logEventLabel = `${rivalName} : ${boardName} : ${cardName}`;

  useEffect(() => {
    const { current } = cardContainer;

    current &&
      current
        .querySelectorAll<HTMLDivElement>('.table-scrollwrap')
        .forEach(initTable);
  }, [cardContainer]);

  useEffect(() => {
    const { current } = cardContainer;

    const onMouseUp: EventListener = (_event) => {
      const selectedText = document.getSelection()?.toString().trim();
      if (selectedText && cardId) {
        logCardInteraction({
          action: AnalyticsAction.highlight,
          label: selectedText,
          cardId,
          rank,
        });
      }
    };
    current?.addEventListener('mouseup', onMouseUp);
    return () => current?.removeEventListener('mouseup', onMouseUp);
  }, [cardContainer, cardId, logCardInteraction, rank]);

  useEffect(() => {
    const { current } = cardContainer;

    const listener = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const href = target.closest('a')?.getAttribute('href');

      if (!target || !current) return false;

      if (
        !current.contains(target as Node) &&
        !target.matches(`[data-card-id='${cardId}'].table-zoom-in a`)
      ) {
        return null;
      }

      if (href) {
        const klueCardRegex =
          /https:\/\/(?:[\w.-]*\.)?klue\.(?:com|d)\/(?:profile\/\d+\?viewCard=\d+|card\/\d+)/;
        if (href && klueCardRegex.test(href)) {
          event.preventDefault();
          try {
            const url = new URL(href);
            const viewCardId =
              url.searchParams.get('viewCard') || url.pathname.split('/').pop();
            if (viewCardId) {
              const currentParams = new URLSearchParams(window.location.search);
              currentParams.delete('viewCard');
              currentParams.append('viewCard', viewCardId);
              navigate({
                pathname: window.location.pathname,
                search: `?${currentParams.toString()}`,
              });
            }
          } catch (error) {
            throw new Error('Error parsing Card URL.');
          }
        }
        logEvent({
          event: {
            category: AnalyticsCategory.curatorAddedLinks,
            action: AnalyticsAction.view,
            label: `${rivalName} : ${boardName} : ${cardName} : ${href}`,
          },
        });

        if (cardId) {
          logCardInteraction({
            action: AnalyticsAction.link,
            label: href,
            cardId,
            rank,
          });
        }

        return;
      }

      if (target.matches('table, table *')) {
        if (target.nodeName === 'A') {
          // Links click should not trigger modal
          return;
        }

        return handleTableClicks({
          e: event,
          onOpenTable: ({ table }) => {
            if (table) {
              let zoomId = 'table0';
              const index = Array.from(
                table.closest('[data-card-id]')?.querySelectorAll('table') ||
                  [],
              ).indexOf(table);
              if (index >= 0) {
                zoomId = `table${index}`;
              }

              logEvent({
                event: {
                  category: AnalyticsCategory.zoom,
                  action: AnalyticsAction.view,
                  label: `${rivalName} : ${boardName} : ${cardName} : ${zoomId}`,
                },
              });

              if (cardId) {
                logCardInteraction({
                  action: AnalyticsAction.table,
                  label: zoomId,
                  cardId,
                  rank,
                });
              }
            }

            return modal.open({
              children: (
                <Box
                  className={`${className} card-static-html_body`.trim()}
                  margin={{ top: 'xsmall' }}
                >
                  <X
                    style={{ alignSelf: 'end', cursor: 'pointer' }}
                    size={tokens.iconSize.xsmall}
                    onClick={modal.close}
                  />
                  <StyledTableScrollWrap
                    data-card-id={cardId}
                    className="table-scrollwrap table-zoom-in"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(table?.outerHTML ?? ''),
                    }}
                    onClick={(event: React.MouseEvent) => {
                      const targ = event?.target as HTMLAnchorElement;

                      targ.classList.contains('card-external-link') &&
                        targ.setAttribute('target', '_blank');
                      listener(event as unknown as MouseEvent);
                    }}
                  />
                </Box>
              ),
              className: 'klue-table-modal',
            });
          },
        });
      }
    };

    current && current.addEventListener('click', listener);

    return () => {
      current && current.removeEventListener('click', listener);
    };
  }, [
    cardContainer,
    cardId,
    cardName,
    boardName,
    rivalName,
    className,
    modal,
    logEvent,
    logCardInteraction,
    rank,
    navigate,
  ]);

  const toggleCardSourcesBox = () => {
    if (cardData?.id) {
      openCardViewer(cardData.id, { scrollToSources: true, rank });
    } else {
      setShowCardSources((s) => !s);
    }
  };

  const handleVisibilityGroupSelected = (
    cardPermissionCardsData?: GetCardPermissionCardsDataResult,
  ) => {
    onVisibilityGroupSelected?.(cardId, cardPermissionCardsData);
  };

  const hasStickyComponent = !hideMenus && !isCardViewerContent;
  const sticky = hasStickyComponent ? (
    <KlueCardToolbar
      sourcesCount={cardData?.sourcesCount || 0}
      cardId={cardId || 0}
      profileId={profileId || 0}
      onFeedbackClick={setFeedbackModal}
      rank={rank}
    />
  ) : null;

  // --- Sticky component behavior ---
  const stickyContainerRef = useRef<HTMLDivElement>(null);
  const [stickyHeight, setStickyHeight] = useState(0);
  const [stickyBottom, setStickyBottom] = useState(0);
  const stickyComponentRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setStickyHeight(node.scrollHeight);
      setStickyBottom(parseInt(node.style.getPropertyValue('bottom') || '0'));
    }
  }, []);
  const [isUserActive, setIsUserActive] = useState(false);
  const [isInView, setIsInView] = useState(true);
  const isHoveringStickyRef = useRef(false);
  const userInactivityTimer = useRef<ReturnType<typeof setTimeout>>();
  const stickyBuffer = isSearchView ? 0 : 73;
  const isStickyHidden = isInView || !isUserActive;

  useEffect(() => {
    if (!hasStickyComponent) return;
    const container = stickyContainerRef.current;
    if (!container) return;

    const threshold = stickyHeight + stickyBottom + stickyBuffer;
    const scrollAncestor =
      (container.closest('.scroll-ancestor') as Element) || null;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.boundingClientRect.height < threshold) {
            setIsInView(true);
            return;
          }
          setIsInView(!entry.isIntersecting);
        });
      },
      {
        root: scrollAncestor,
        rootMargin: `0px 0px -${threshold}px 0px`,
        threshold: [0],
      },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [hasStickyComponent, stickyBuffer, stickyHeight, stickyBottom]);

  const handleMouseMove = useMemo(
    () =>
      throttle(() => {
        clearTimeout(userInactivityTimer.current);
        if (!isHoveringStickyRef.current) {
          setIsUserActive(true);
          userInactivityTimer.current = setTimeout(
            () => setIsUserActive(false),
            USER_INACTIVITY_MS,
          );
        }
      }, THROTTLE_MS),
    [],
  );

  useEffect(() => {
    return () => {
      handleMouseMove.cancel();
      clearTimeout(userInactivityTimer.current);
    };
  }, [handleMouseMove]);

  const displayVisibilityGroups =
    !isSearchView &&
    !isCardViewerContent &&
    bulkEditOption !== BulkEditOption.Tags &&
    cardData &&
    ((isCurator && isNewCardEditorEnabled) ||
      (isSelectable && isEditingCardPermissions));

  const displayCardInfo =
    !isSearchView &&
    (showCardInfo || isCurator || !!bulkEditOption) &&
    !isCardViewerContent &&
    cardData &&
    !cardData?.data?.hasDynamicBlocks;

  const displayScheduleIcon =
    hasSchedule && !isSearchView && !isCardViewerContent && isCurator;

  const { isDraft, allAccess, visibilityGroups } = cardData || {};

  const cardBorderStyle = useMemo(() => {
    if (isSearchView || isCardViewerContent) {
      return 'none';
    }

    if (!hasLoaded) {
      return `${VisibilityGroupBorderWidth} solid ${tokens.color.neutral.lightgrey.main}`;
    }

    if (isCurator) {
      const visibilityColors = getVisibilityGroupColors({
        isDraft: isDraft ?? false,
        allAccess: allAccess ?? false,
        visibilityGroups: visibilityGroups ?? [],
      });
      return `${VisibilityGroupBorderWidth} solid ${visibilityColors.border}`;
    }

    return `${VisibilityGroupBorderWidth} solid ${tokens.color.neutral.lightgrey.main}`;
  }, [
    isSearchView,
    isCardViewerContent,
    hasLoaded,
    isCurator,
    isDraft,
    allAccess,
    visibilityGroups,
  ]);

  const cardShadow = isCardViewerContent
    ? tokens.boxShadow.none
    : tokens.boxShadow[cardElevation];

  const overflowStyle = useMemo((): React.CSSProperties => {
    if (!overflow) return {};
    if (typeof overflow === 'string') return { overflow };
    return {
      overflowX: overflow.horizontal,
      overflowY: overflow.vertical,
    };
  }, [overflow]);

  const paperStyle = useMemo(
    (): React.CSSProperties => ({
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      flexShrink: 0,
      border: cardBorderStyle,
      boxShadow: cardShadow,
      background: tokens.color.neutral.white.main,
      borderRadius: tokens.borderRadius.xsmall,
      width: isBattlecardView || fluid ? '100%' : CARD_WIDTH,
      maxWidth: '100%',
      ...(isBattlecardView ? { height: '100%' } : {}),
      overflow: 'hidden',
      ...overflowStyle,
      ...(isCardViewerContent ? {} : padStyles),
    }),
    [
      cardBorderStyle,
      cardShadow,
      isBattlecardView,
      fluid,
      CARD_WIDTH,
      overflowStyle,
      isCardViewerContent,
      padStyles,
    ],
  );

  const stickyContainerStyles = getStickyStyles({ variant });

  const cardContent = (
    <Paper
      data-card-id={cardId}
      data-test-id={TEST_IDS.klueCard.container}
      //PS: DON'T REMOVE this is used to consume card-static-html.css from v1
      className={`${className} card-static-html`.trim()}
      ref={cardContainer}
      style={
        hasStickyComponent
          ? paperStyle
          : { ...stickyContainerStyles, ...paperStyle }
      }
      shadow={undefined}
      radius={0}
    >
      {isSelectable && (
        <Box margin={{ bottom: 'xlarge' }}>
          <CardSelector
            format="card"
            id={cardId}
            isSelected={isSelected}
            onToggleSelected={onToggleSelected}
          />
        </Box>
      )}
      <KlueCardHeader
        {...props}
        onTitleHover={(isHover) => {
          if (isSearchView) {
            onTitleHover?.(isHover);
          } else {
            setCardElevation(() => (isHover ? 'large' : 'xsmall'));
          }
        }}
        onTitleClick={onTitleClick}
        hideMenus={hideMenus}
        hideBattlecardIndicator={hideBattlecardIndicator}
        rank={rank}
      />
      <KlueCardContent
        hasLoaded={hasLoaded}
        isCardViewerContent={isCardViewerContent}
        cardData={cardData?.data}
        initialHeight={initialHeight}
        cardId={cardId}
        logEvent={logEvent}
        rank={rank}
        logCardInteraction={logCardInteraction}
        logEventLabel={logEventLabel}
        KLUE_CARD_CUSTOM_MARGIN_CONTENT={KLUE_CARD_CUSTOM_MARGIN_CONTENT}
        KLUE_CARD_INTERNAL_GUTTER={KLUE_CARD_INTERNAL_GUTTER}
        showAiProcessingPlaceholder={showAiProcessingPlaceholder}
        aiProcessingFrequencyDays={aiProcessingFrequencyDays}
      />

      {isCardSentimentEnabled && showCardSentiment && cardId && !isCurator && (
        <KlueCardSentiment cardId={cardId} rank={rank} />
      )}

      {hasLoaded && (
        <>
          <KlueCardTagEditor
            cardId={cardId || undefined}
            cardData={cardData}
            showTags={showTags}
            analytics={{
              category:
                (isBattlecardView && AnalyticsCategory.battlecard) ||
                (isSearchView && AnalyticsCategory.search) ||
                AnalyticsCategory.profile,
            }}
            isCardViewerContent={isCardViewerContent}
            shouldOpenInNewTab={isEmbedded}
            rank={rank}
          />

          {displayVisibilityGroups || displayScheduleIcon || displayCardInfo ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flexShrink: 0,
                gap: 0,
                width: 'auto',
                margin: `0 ${tokens.spacing.large} ${tokens.spacing.large} ${tokens.spacing.large}`,
                ...(isBattlecardView
                  ? { paddingBottom: tokens.spacing.small }
                  : {}),
              }}
            >
              <Box
                direction="row"
                align="center"
                gap="small"
                justify="between"
                fill="horizontal"
              >
                {(displayVisibilityGroups || displayScheduleIcon) && (
                  <Box
                    direction="row"
                    align="center"
                    gap="xsmall"
                    flex={{ shrink: 0 }}
                  >
                    {displayVisibilityGroups && (
                      <KlueCardVisibilityGroups
                        cardId={cardId}
                        isDraft={isDraft}
                        visibilityGroups={visibilityGroups}
                        allAccess={allAccess}
                        displayInModal={true}
                        onVisibilityGroupSelected={
                          !!bulkEditOption
                            ? undefined
                            : handleVisibilityGroupSelected
                        }
                      />
                    )}
                    {displayScheduleIcon && (
                      <Tip
                        content={t('Card:schedule.tooltip')}
                        color="dark"
                        variant="xsmall"
                      >
                        <Box
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setShowScheduleModal(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <CalendarBlank size={tokens.iconSize.small} />
                        </Box>
                      </Tip>
                    )}
                  </Box>
                )}
                {displayCardInfo && (
                  <KlueCardInfo
                    card={cardData}
                    sourcesCount={cardData?.sourcesCount}
                    hasCardSourcesIcon={!isSelectable && !isBattlecardView}
                    onCardSourcesClick={toggleCardSourcesBox}
                    shouldOpenInNewTab={isEmbedded}
                  />
                )}
              </Box>
            </div>
          ) : null}
        </>
      )}

      {cardId && showCardSources && (
        <Suspense fallback={<div />}>
          <KlueCardSources
            cardId={cardId}
            width={CARD_WIDTH}
            onClose={toggleCardSourcesBox}
            sourcesCount={cardData?.sourcesCount || 0}
            isCardViewerContent={isCardViewerContent}
          />
        </Suspense>
      )}
      {cardId && cardData && (
        <KlueCardFeedbackModal
          isFeedbackModalOpen={feedbackModal}
          onClickModal={setFeedbackModal}
          cardId={cardId}
          cardName={cardName}
          rivalId={rivalId}
          profileId={cardData.board?.profileId || profileId}
        />
      )}
      {showScheduleModal && cardId && (
        <CardScheduleModal
          cardId={cardId}
          onSave={() => setShowScheduleModal(false)}
          onClose={() => setShowScheduleModal(false)}
        />
      )}
    </Paper>
  );

  if (sticky) {
    return (
      <div
        style={{
          ...stickyContainerStyles,
          position: 'relative',
          maxWidth: '100%',
        }}
        onMouseEnter={() => setIsUserActive(true)}
        onMouseLeave={() => setIsUserActive(false)}
        onMouseMove={handleMouseMove}
        ref={stickyContainerRef}
      >
        {cardContent}
        <div
          ref={stickyComponentRef}
          onMouseEnter={() => {
            isHoveringStickyRef.current = true;
          }}
          onMouseLeave={() => {
            isHoveringStickyRef.current = false;
          }}
          style={{
            position: 'sticky',
            bottom: 0,
            opacity: isStickyHidden ? 0 : 1,
            pointerEvents: isStickyHidden ? 'none' : 'auto',
            transition: STICKY_TRANSITION,
          }}
        >
          {sticky}
        </div>
      </div>
    );
  }

  return cardContent;
};

export default KlueCard;
